import type { LucideIcon } from 'lucide-react';
import {
  BookText,
  Calculator,
  Dumbbell,
  FlaskConical,
  Languages,
  Map,
  Music2,
  Settings2,
} from 'lucide-react';
import type { GameMode } from '@interactive-room';
import { STANDALONE_ACTIVITY_MODES, isStandaloneActivityMode } from './standaloneActivityModes';

export interface SubjectDefinition {
  id: string;
  tag: string;
  name: string;
  /** Pictograma emoji (legacy / tarjetas densas). Preferir `getSubjectLucideIcon`. */
  icon: string;
  hue: string;
  tint: string;
  description: string;
  /** Ejemplo de clase real — visible en landing */
  homeHook: string;
  /** Andamiaje oculto para prompts de IA */
  aiExpertPrompt: string;
}

/** Iconos SVG del rail / chrome — mismo lenguaje visual que Cursos · Crear · Taller. */
export const SUBJECT_LUCIDE_ICONS: Record<string, LucideIcon> = {
  matematica: Calculator,
  lenguaje: BookText,
  musica: Music2,
  ciencias: FlaskConical,
  historia: Map,
  ingles: Languages,
  educacion_fisica: Dumbbell,
  general: Settings2,
};

export function getSubjectLucideIcon(subjectId: string): LucideIcon {
  return SUBJECT_LUCIDE_ICONS[subjectId] ?? Settings2;
}

export type RecipeCategory = 'activador' | 'laboratorio' | 'cooperativo' | 'cierre';

export interface RecipeCategoryMeta {
  id: RecipeCategory;
  icon: string;
  label: string;
}

export const RECIPE_CATEGORIES: Record<RecipeCategory, RecipeCategoryMeta> = {
  activador: { id: 'activador', icon: '🚀', label: 'Activador rápido' },
  laboratorio: { id: 'laboratorio', icon: '🔎', label: 'Laboratorio interactivo' },
  cooperativo: { id: 'cooperativo', icon: '🧩', label: 'Reto cooperativo' },
  cierre: { id: 'cierre', icon: '📝', label: 'Misión de cierre' },
};

export interface AICaseRecipe {
  id: string;
  subjectId: string;
  title: string;
  description: string;
  icon: string;
  mode: GameMode;
  category: RecipeCategory;
  /** Instrucción oculta fusionada con el prompt de IA */
  aiSystemPromptExtension: string;
  placeholderText: string;
  /** Enlaza a ACTIVITY_PRESETS cuando existe demo de respaldo */
  presetId?: string;
}

/** @deprecated Alias — misma forma que AICaseRecipe */
export type SubjectUseCase = AICaseRecipe;

export const SUBJECT_CATALOG: SubjectDefinition[] = [
  {
    id: 'matematica',
    tag: 'matematica',
    name: 'Matemáticas',
    icon: '🧮',
    hue: '#5eead4',
    tint: '#ecfdf5',
    description: 'Procedimientos, geometría y razonamiento numérico.',
    homeHook: '«Hoy me toca pasar fracciones con el 5° básico»',
    aiExpertPrompt:
      'Eres un experto en pedagogía matemática para educación básica y media en Chile. Usa lenguaje claro, ejemplos concretos del currículum y progresión cognitiva.',
  },
  {
    id: 'lenguaje',
    tag: 'lenguaje',
    name: 'Lenguaje y Comunicación',
    icon: '📚',
    hue: '#c084fc',
    tint: '#f5f0ff',
    description: 'Comprensión lectora, escritura y argumentación.',
    homeHook: '«Vamos a trabajar conectores con el 6° básico»',
    aiExpertPrompt:
      'Eres un experto en pedagogía del lenguaje y comunicación. Prioriza comprensión lectora, conectores lógicos y producción textual significativa.',
  },
  {
    id: 'musica',
    tag: 'musica',
    name: 'Música',
    icon: '🎵',
    hue: '#38bdf8',
    tint: '#f0f9ff',
    description: 'Teoría, lectura y estructura musical en vivo.',
    homeHook: '«Clase de Música con el 7° básico — estructura de una canción»',
    aiExpertPrompt:
      'Eres un experto en pedagogía musical. Adapta actividades al nivel del curso, con vocabulario musical preciso pero accesible. Respeta el método de lectura elegido por el docente (tradicional, Takadimi o Kodály) y usa tokens rítmicos W/H/Q/E/S/R_Q en lugar de texto libre.',
  },
  {
    id: 'ciencias',
    tag: 'ciencias',
    name: 'Ciencias Naturales',
    icon: '🧪',
    hue: '#9ae6b4',
    tint: '#f0fff4',
    description: 'Experimentos, procesos y fenómenos del entorno.',
    homeHook: '«Repaso del ciclo del agua antes de la prueba»',
    aiExpertPrompt:
      'Eres un experto en pedagogía de ciencias naturales. Enfatiza indagación, evidencia y relación con el entorno cotidiano chileno.',
  },
  {
    id: 'historia',
    tag: 'historia',
    name: 'Historia y Geografía',
    icon: '🗺️',
    hue: '#fbbf24',
    tint: '#fffbeb',
    description: 'Líneas de tiempo, mapas y debate sobre el pasado.',
    homeHook: '«Línea de tiempo de la Independencia con el 8° básico»',
    aiExpertPrompt:
      'Eres un experto en pedagogía de historia y geografía. Contextualiza hechos, causas y consecuencias con mirada crítica y territorial.',
  },
  {
    id: 'ingles',
    tag: 'ingles',
    name: 'Inglés',
    icon: '🔤',
    hue: '#fb7185',
    tint: '#fff1f2',
    description: 'Vocabulario, comunicación y comprensión en lengua inglesa.',
    homeHook: '«Practicar rutinas orales con el 6° básico»',
    aiExpertPrompt:
      'Eres un experto en pedagogía de inglés como lengua extranjera (EFL) en Chile. Prioriza uso comunicativo, input comprensible y práctica en contexto; no conviertas la clase en listas de gramática descontextualizada.',
  },
  {
    id: 'educacion_fisica',
    tag: 'educacion_fisica',
    name: 'Educación Física y Salud',
    icon: '🏃',
    hue: '#34d399',
    tint: '#ecfdf5',
    description: 'Motricidad, juego, deporte y hábitos de vida activa.',
    homeHook: '«Circuito de habilidades motrices con el 4° básico»',
    aiExpertPrompt:
      'Eres un experto en pedagogía de Educación Física y Salud en Chile. Prioriza motricidad, juego cooperativo, seguridad, inclusión y hábitos de vida activa; adapta demandas al nivel etario y evita tecnicismos deportivos innecesarios.',
  },
  {
    id: 'general',
    tag: 'general',
    name: 'General / Herramientas libres',
    icon: '⚙️',
    hue: '#94a3b8',
    tint: '#f1f5f9',
    description: 'Acceso directo a los motores sin filtro de asignatura.',
    homeHook: 'Para quien ya sabe qué mecánica quiere usar.',
    aiExpertPrompt:
      'Eres un diseñador instruccional versátil. Adapta la actividad al contexto que indique el docente.',
  },
];

export const SUBJECT_USE_CASES: AICaseRecipe[] = [
  // ── Matemáticas ──
  {
    id: 'mat-balanza',
    subjectId: 'matematica',
    title: 'Equilibrar una balanza algebraica',
    description: 'Despeja ecuaciones moviendo x e y como fichas — la clase ve el balance en pantalla.',
    icon: '⚖️',
    mode: 'sandbox',
    category: 'laboratorio',
    presetId: 'sandbox-ecuaciones',
    aiSystemPromptExtension:
      'Configura sandbox variant "balanza" con skin "algebra" y balanceMode "inverse-operations". Define equationLeft {xBoxes, constants} y equationRight {xBoxes:0, constants} para una ecuación lineal equilibrada desde el inicio (ej: 2x+4=10 → left {2,4} right {0,10}). availableVariables deben ser OPERACIONES GLOBALES tipo BOTH_SUBTRACT, BOTH_DIVIDE, BOTH_ADD aplicadas a ambos lados — incluye las operaciones correctas para despejar x y 1-2 distractores. displayEquation debe reflejar el estado inicial.',
    placeholderText: 'Ej: 3x + 2 = 11, ecuaciones con fracciones simples o sistemas lineales básicos…',
  },
  {
    id: 'mat-coordenada',
    subjectId: 'matematica',
    title: 'Ubicar un punto en el plano',
    description: '¿Dónde cae (3, 2)? Los alumnos marcan la coordenada sobre el gráfico proyectado.',
    icon: '📍',
    mode: 'point',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura point con prompt que pida ubicar una coordenada (x, y) en el plano cartesiano. target debe tener x e y entre 0 y 1 (coordenadas normalizadas). Usa imageUrl SVG de un plano cartesiano simple con ejes visibles.',
    placeholderText: 'Ej: Ubicar (3, 2), el vértice de un triángulo o el punto de intersección…',
  },
  {
    id: 'mat-secuencia',
    subjectId: 'matematica',
    title: 'Ordenar los pasos de un procedimiento',
    description: 'Desde plantear el problema hasta la respuesta final — secuencia en equipo.',
    icon: '🔢',
    mode: 'order',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura order con 4-6 pasos concretos de un procedimiento matemático (resolver ecuación, calcular área, etc.). items deben ser pasos reales desordenables; correctOrder es [0,1,2,...].',
    placeholderText: 'Ej: Pasos para resolver una ecuación con paréntesis, o calcular el área de un triángulo…',
  },
  {
    id: 'mat-cuestionario',
    subjectId: 'matematica',
    title: 'Repaso con podio',
    description: 'Preguntas de fracciones, geometría o lo que toque hoy — con puntaje y energía de sala.',
    icon: '🏆',
    mode: 'arena',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura arena con 4-6 preguntas de opción múltiple sobre el tema matemático indicado. Cada pregunta con 4 opciones plausibles y correctIndex válido. Incluye timeLimitSec: 30 y points: 100.',
    placeholderText: 'Ej: Fracciones equivalentes, perímetro de figuras, operaciones con enteros…',
  },
  {
    id: 'mat-encuesta',
    subjectId: 'matematica',
    title: '¿Qué tan seguros están?',
    description: 'Encuesta anónima antes o después de la unidad para calibrar la clase.',
    icon: '📊',
    mode: 'poll',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura poll con 1 slide de opción múltiple sobre autoconfianza o percepción del tema (ej: "¿Qué tan seguro te sientes con ecuaciones?"). 4 opciones graduadas. settings.anonymous: true.',
    placeholderText: 'Ej: Confianza con fracciones, dificultad percibida del tema de hoy…',
  },

  // ── Lenguaje ──
  {
    id: 'len-anagrama',
    subjectId: 'lenguaje',
    title: 'Fábrica de anagramas',
    description: 'Letras compartidas en equipo para descubrir la palabra del texto o la unidad.',
    icon: '🔤',
    mode: 'coop',
    category: 'cooperativo',
    presetId: 'coop-anagram',
    aiSystemPromptExtension:
      'Configura coop board kind "anagram" con palabras y pistas del vocabulario del texto o unidad leída. Incluye al menos 3 palabras con pistas concretas.',
    placeholderText: 'Ej: Palabras clave del cuento "El principito", conectores del texto leído…',
  },
  {
    id: 'len-conectores',
    subjectId: 'lenguaje',
    title: 'Conectores en cadena',
    description: 'Cada alumno aporta el conector que falta: sin embargo, por lo tanto, además…',
    icon: '🔗',
    mode: 'coop',
    category: 'cooperativo',
    presetId: 'coop-chain',
    aiSystemPromptExtension:
      'Configura coop board kind "chain" con oraciones incompletas que requieran conectores lógicos (causa, contraste, adición). Cada eslabón debe tener un conector esperado.',
    placeholderText: 'Ej: Conectores de contraste y causa en un texto argumentativo…',
  },
  {
    id: 'len-argumentos',
    subjectId: 'lenguaje',
    title: 'Ordenar los argumentos de un texto',
    description: 'Secuencia tesis, evidencias y conclusión de una opinión o artículo.',
    icon: '📝',
    mode: 'order',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura order con 4-6 ítems que representen partes de un texto argumentativo (tesis, evidencias, contraargumento, conclusión) en orden lógico.',
    placeholderText: 'Ej: Estructura de una carta al director, artículo de opinión sobre…',
  },
  {
    id: 'len-debate',
    subjectId: 'lenguaje',
    title: 'Debate a favor y en contra',
    description: 'Murales con posturas breves sobre una afirmación del texto leído.',
    icon: '💬',
    mode: 'wall',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura wall con prompt de debate sobre una afirmación del texto leído. stances: "A favor" y "En contra" con colores distintos. maxChars: 140.',
    placeholderText: 'Ej: "El personaje tenía razón en…", "La autora defiende que…"',
  },
  {
    id: 'len-comprension',
    subjectId: 'lenguaje',
    title: 'Comprobación de comprensión lectora',
    description: 'Ideas principales, inferencias y vocabulario — con respuestas instantáneas.',
    icon: '📖',
    mode: 'arena',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura arena con 4-5 preguntas de comprensión lectora (idea principal, inferencia, vocabulario en contexto) con 4 opciones cada una.',
    placeholderText: 'Ej: Preguntas sobre el cuento leído, inferencias sobre el protagonista…',
  },
  {
    id: 'len-encuesta',
    subjectId: 'lenguaje',
    title: 'Encuesta sobre el texto',
    description: '¿Les gustó? ¿Fue difícil? Opinión rápida antes de la producción escrita.',
    icon: '🗳️',
    mode: 'poll',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura poll con 1 slide sobre opinión del texto leído (gusto, dificultad, identificación con personajes). 4 opciones. settings.anonymous: true.',
    placeholderText: 'Ej: ¿Qué te pareció el final?, ¿Identificaste con algún personaje?…',
  },

  // ── Música — módulos nativos ──
  {
    id: 'mus-rhythm-canvas',
    subjectId: 'musica',
    title: 'Pista rítmica visual',
    description: 'Compás con figuras reales — completar, ordenar o identificar sin texto genérico.',
    icon: '🥁',
    mode: 'rhythm_canvas',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Usa mode rhythm_canvas con patternTokens (Q/E/H/S/W/R_Q), timeSignature, taskKind complete|order|identify. PROHIBIDO flechas ←, ecuaciones "Negra+Negra" o basura 9999. El renderizador dibuja | ♩ ♫ ♩ ♫ |.',
    placeholderText: 'Ej: Compás 4/4 con negras y corcheas, completar figura faltante…',
  },
  {
    id: 'mus-melody-staff',
    subjectId: 'musica',
    title: 'Nota en el pentagrama',
    description: 'Fragmento de pentagrama vectorial — el alumno identifica la nota en clave de Sol.',
    icon: '🎹',
    mode: 'melody_staff',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Usa mode melody_staff con notes:[{ pitch:"G4", duration:"Q" }], clef treble, taskKind identify, answerLabel en español (Sol, Do, Mi…).',
    placeholderText: 'Ej: Identificar Sol en 2do espacio, Do central en 3er espacio…',
  },
  {
    id: 'mus-ear-training',
    subjectId: 'musica',
    title: 'Entrenamiento auditivo',
    description: 'Dictado con audio nativo en digital y guía docente en papel.',
    icon: '👂',
    mode: 'ear_training',
    category: 'cierre',
    aiSystemPromptExtension:
      'Usa mode ear_training con teacherScript explícito, noteNames ["Do","Mi","Sol"] o patternTokens rítmicos. En papel solo guía docente — sin reproductor.',
    placeholderText: 'Ej: Tríada Do-Mi-Sol, patrón rítmico de corcheas y negras…',
  },

  // ── Música (legacy / complemento) ──
  {
    id: 'mus-compases-takadimi',
    subjectId: 'musica',
    title: 'Construir compases rítmicos',
    description: 'Ordena las figuras rítmicas para completar la métrica correcta del compás.',
    icon: '🥁',
    mode: 'order',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura order con printLayout "rhythm-bar", meter "4/4" o "3/4", patternTokens (W/H/Q/E/S/R_Q) y rhythmCells paralelos. El renderizador muestra ♩ ♫ 𝅗𝅥 — NO escribas "(negra)" ni códigos W/Q al alumno. correctOrder representa la subdivisión correcta del compás.',
    placeholderText: 'Ej: Compás 4/4 con negras y corcheas, compás 3/4 con blancas y silencios…',
  },
  {
    id: 'mus-dictado-ritmico',
    subjectId: 'musica',
    title: 'Dictado e identificación rítmica',
    description: 'Escucha el patrón en sala y transcribe — con guía docente en la hoja impresa.',
    icon: '👂',
    mode: 'dictado',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura dictado con studentPrompt SIN "Escucha", teacherScript obligatorio, patternTokens (ej. ["E","Q","E","Q"]), meter "4/4", transcriptionLines: 4. Usa vocabulario chileno (negra, blanca, corchea).',
    placeholderText: 'Ej: Patrón de corcheas y negras en 4/4, dictado con silencio de negra…',
  },
  {
    id: 'mus-anatomia-figuras',
    subjectId: 'musica',
    title: 'Anatomía y duración de figuras',
    description: '¿Cuántos tiempos vale cada figura? Blanca = 2, Negra = 1 — rigor matemático-musical.',
    icon: '🎼',
    mode: 'anatomia',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura anatomia con items usando token (Q/H/E/S/W/R_Q) y correctBeats exacto (Blanca=2, Negra=1, Corchea=0.5, Semicorchea=0.25, Redonda=4). figureLabel en español chileno. PROHIBIDO decir que la Blanca vale medio tiempo.',
    placeholderText: 'Ej: Duración de negra, blanca y corchea en compás 4/4…',
  },
  {
    id: 'mus-teoria-cifrado',
    subjectId: 'musica',
    title: 'Teoría musical y cifrado',
    description: 'Empareja los acordes con su cifrado americano o su escala.',
    icon: '🎹',
    mode: 'vincula',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura vincula con 4-6 pares limpios: ladoA nombre del acorde en español (ej. "Do Mayor Séptima") y ladoB cifrado americano (ej. "Cmaj7") o notas constituyentes. Sin ambigüedades ni pares duplicados.',
    placeholderText: 'Ej: Do Mayor / C, La menor / Am, acordes de séptima y sus extensiones…',
  },
  {
    id: 'mus-estructura',
    subjectId: 'musica',
    title: 'Ordenar la estructura de una canción',
    description: 'Intro → estrofa → coro → solo → outro. La clase arma la secuencia correcta.',
    icon: '🕒',
    mode: 'order',
    category: 'laboratorio',
    presetId: 'sequencer-estructura',
    aiSystemPromptExtension:
      'Configura order con 5-6 bloques estructurales de una canción (Intro, Verso, Coro, Puente, Solo, Outro) como ítems ordenables.',
    placeholderText: 'Ej: Estructura de una balada pop, forma de un vals, secciones de una cueca…',
  },
  {
    id: 'mus-partitura',
    subjectId: 'musica',
    title: 'Señalar en la partitura',
    description: 'Toca la clave de sol, la nota o el símbolo que indiques en la imagen.',
    icon: '🎼',
    mode: 'point',
    category: 'laboratorio',
    presetId: 'pinpoint-partitura',
    aiSystemPromptExtension:
      'Configura point con prompt para señalar un elemento musical en partitura (clave, nota, compás). target con coordenadas normalizadas 0-1.',
    placeholderText: 'Ej: Señalar la clave de sol, la nota Do en 3er espacio, el silencio de negra…',
  },
  {
    id: 'mus-crucigrama',
    subjectId: 'musica',
    title: 'Crucigrama de teoría musical',
    description: 'Armado colaborativo: armadura, compás, dinámica, figuras rítmicas…',
    icon: '🧩',
    mode: 'coop',
    category: 'cooperativo',
    aiSystemPromptExtension:
      'Configura coop board kind "crossword" con 4-6 palabras de teoría musical y pistas concretas (armadura, compás, dinámica, figuras rítmicas).',
    placeholderText: 'Ej: Vocabulario de ritmo y métrica, términos de intervalos y escalas…',
  },
  {
    id: 'mus-lienzo',
    subjectId: 'musica',
    title: 'Dictado rítmico o melódico',
    description: 'Un alumno dibuja el contorno del fragmento; la sala adivina qué es.',
    icon: '🎨',
    mode: 'coop',
    category: 'cooperativo',
    presetId: 'coop-melody-sketch',
    aiSystemPromptExtension:
      'Configura coop board kind "melody-sketch" o "pictionary" con prompt musical (ritmo, melodía, instrumento). Incluye pista inicial para el dibujante.',
    placeholderText: 'Ej: Ritmo de samba, contorno melódico de una escala mayor…',
  },
  {
    id: 'mus-encuesta',
    subjectId: 'musica',
    title: '¿Qué escuchaste?',
    description: 'Opina sobre un fragmento: tempo, instrumento, estilo o emoción.',
    icon: '👂',
    mode: 'poll',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura poll con 1 slide sobre percepción auditiva (tempo, instrumento principal, emoción, estilo). 4 opciones concretas.',
    placeholderText: 'Ej: ¿Qué instrumento predominó?, ¿Era rápido o lento?, emoción del fragmento…',
  },

  // ── Ciencias ──
  {
    id: 'cie-experimento',
    subjectId: 'ciencias',
    title: 'Ordenar las fases del experimento',
    description: 'Hipótesis → materiales → procedimiento → observación → conclusión.',
    icon: '🔬',
    mode: 'order',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura order con 5-6 pasos del método científico aplicados a un experimento concreto del tema indicado.',
    placeholderText: 'Ej: Experimento de germinación, reacción química simple, observación de células…',
  },
  {
    id: 'cie-diagrama',
    subjectId: 'ciencias',
    title: 'Señalar en un diagrama',
    description: 'Órganos, capas de la Tierra, partes de la célula — la clase marca en la imagen.',
    icon: '🫀',
    mode: 'point',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura point con prompt para señalar una parte en un diagrama científico (órgano, capa, organelo). target con coordenadas normalizadas.',
    placeholderText: 'Ej: Núcleo celular, capa de ozono, órgano del sistema digestivo…',
  },
  {
    id: 'cie-crucigrama',
    subjectId: 'ciencias',
    title: 'Crucigrama del vocabulario',
    description: 'Ecosistema, fotosíntesis, cadena alimenticia… en equipo y en vivo.',
    icon: '🧬',
    mode: 'coop',
    category: 'cooperativo',
    aiSystemPromptExtension:
      'Configura coop board kind "crossword" con 4-6 términos científicos del tema y pistas precisas.',
    placeholderText: 'Ej: Vocabulario del ciclo del agua, ecosistema, fotosíntesis…',
  },
  {
    id: 'cie-cuestionario',
    subjectId: 'ciencias',
    title: 'Repaso de la unidad',
    description: 'Verifica contenidos con la energía de una sala conectada.',
    icon: '⚡',
    mode: 'arena',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura arena con 4-6 preguntas de ciencias sobre la unidad indicada. Opciones plausibles y correctIndex válido.',
    placeholderText: 'Ej: Ciclo del agua, sistemas del cuerpo humano, fuerzas y movimiento…',
  },
  {
    id: 'cie-encuesta',
    subjectId: 'ciencias',
    title: 'Ideas previas al tema',
    description: '¿Qué creen que pasa cuando…? Recoge concepciones antes de explicar.',
    icon: '🌱',
    mode: 'poll',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura poll con 1 slide de ideas previas (concepciones alternativas) sobre un fenómeno natural. 4 opciones sin respuesta única correcta.',
    placeholderText: 'Ej: ¿Qué pasa si calientas hielo?, ¿De dónde viene la lluvia?, ideas sobre gravedad…',
  },

  // ── Historia y Geografía ──
  {
    id: 'his-linea',
    subjectId: 'historia',
    title: 'Armar una línea de tiempo',
    description: 'Hitos, causas y consecuencias en orden cronológico — la clase ordena en vivo.',
    icon: '📅',
    mode: 'order',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura order con 5-6 hitos históricos del periodo indicado en orden cronológico. items con fechas o periodos claros.',
    placeholderText: 'Ej: Independencia de Chile, Revolución Industrial, proceso democrático…',
  },
  {
    id: 'his-gobernabilidad',
    subjectId: 'historia',
    title: 'Tomar decisiones en una crisis',
    description: 'Simula gobernar ante un conflicto histórico: recursos, apoyo y consecuencias.',
    icon: '🏛️',
    mode: 'sandbox',
    category: 'laboratorio',
    presetId: 'sandbox-governance',
    aiSystemPromptExtension:
      'Configura sandbox variant "inflation-market" como simulador de gobernabilidad. productName: índice de aprobación o estabilidad. basePrice y targetPrice numéricos. 2 equipos con modifiers temáticos del conflicto histórico.',
    placeholderText: 'Ej: Crisis económica 1982, conflicto social, decisión ante una catástrofe…',
  },
  {
    id: 'his-mapa',
    subjectId: 'historia',
    title: 'Ubicar en el mapa',
    description: 'Batallas, rutas comerciales o regiones de Chile — señala el lugar exacto.',
    icon: '📍',
    mode: 'point',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura point con prompt para ubicar un lugar histórico o geográfico en un mapa. target con coordenadas normalizadas. imageUrl SVG de mapa simplificado.',
    placeholderText: 'Ej: Batalla de Maipú, ruta del estrecho, región de Atacama…',
  },
  {
    id: 'his-debate',
    subjectId: 'historia',
    title: 'Debate sobre un hecho histórico',
    description: 'Posturas breves: ¿fue necesaria? ¿qué habrías hecho tú? Mural a favor / en contra.',
    icon: '⚖️',
    mode: 'wall',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura wall con prompt de debate histórico (causa, consecuencia, decisión de un líder). stances a favor/en contra.',
    placeholderText: 'Ej: ¿Fue justificada la independencia?, ¿Qué habrías hecho en…?',
  },
  {
    id: 'his-cuestionario',
    subjectId: 'historia',
    title: 'Repaso de fechas y conceptos',
    description: 'Repasa el periodo en juego con preguntas y podio.',
    icon: '🏆',
    mode: 'arena',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura arena con 4-6 preguntas de historia/geografía del periodo indicado. Fechas, causas, conceptos clave.',
    placeholderText: 'Ej: Repaso Independencia, geografía de Chile, proceso democrático…',
  },
  {
    id: 'his-encuesta',
    subjectId: 'historia',
    title: 'Encuesta de opinión histórica',
    description: '¿Qué piensan sobre…? Sin respuesta correcta — activa la conversación.',
    icon: '🗳️',
    mode: 'poll',
    category: 'activador',
    aiSystemPromptExtension:
      'Configura poll con 1 slide de opinión sobre un hecho o dilema histórico. 4 posturas posibles, sin respuesta correcta.',
    placeholderText: 'Ej: ¿Fue un héroe o un villano?, ¿Qué legado dejó…?',
  },

  // ── Inglés (EFL) ──
  {
    id: 'eng-cloze',
    subjectId: 'ingles',
    title: 'Cloze con diálogo',
    description: 'Completar huecos deducibles de un diálogo o texto breve en inglés.',
    icon: '🔤',
    mode: 'completar',
    category: 'laboratorio',
    aiSystemPromptExtension:
      'Configura completar con un diálogo o párrafo corto en inglés visible (stimulus o en el text). UN solo [corchete] por oración; answer deducible del contexto. Distractors plausibles en inglés. PROHIBIDO gramática descontextualizada.',
    placeholderText: 'Ej: At the cafeteria — ordering food, classroom routines…',
  },
  {
    id: 'eng-order-dialog',
    subjectId: 'ingles',
    title: 'Ordenar la conversación',
    description: 'Secuencia lógica de turnos en un diálogo cotidiano.',
    icon: '💬',
    mode: 'order',
    category: 'cooperativo',
    aiSystemPromptExtension:
      'Configura order con 4-6 turnos de un diálogo en inglés (saludos, pedido, respuesta). items son frases reales; correctOrder [0,1,2,...].',
    placeholderText: 'Ej: Greeting a new classmate, buying tickets…',
  },
  {
    id: 'eng-vocab-set',
    subjectId: 'ingles',
    title: 'Lexical set',
    description: 'Glosario visual de un conjunto léxico (school, food, weather…).',
    icon: '📋',
    mode: 'arena',
    category: 'cierre',
    aiSystemPromptExtension:
      'Configura arena con 4-6 preguntas de vocabulario en contexto (no traducción aislada). Opciones en inglés cuando sea posible; correctIndex válido.',
    placeholderText: 'Ej: Weather words, classroom objects…',
  },
];

/** Modos con editor en Studio (excluye bloques solo-evaluación) */
export const STUDIO_BUILDER_MODES = STANDALONE_ACTIVITY_MODES;

export type StudioBuilderMode = (typeof STUDIO_BUILDER_MODES)[number];

export function isStudioBuilderMode(mode: string): mode is StudioBuilderMode {
  return isStandaloneActivityMode(mode);
}

export function getSubjectById(id: string): SubjectDefinition | undefined {
  return SUBJECT_CATALOG.find((s) => s.id === id);
}

export function getSubjectByTag(tag: string | null | undefined): SubjectDefinition | undefined {
  const normalized = String(tag || '').trim().toLowerCase();
  if (!normalized) return undefined;
  return SUBJECT_CATALOG.find((s) => s.tag === normalized || s.id === normalized);
}

export function getSubjectNameByTag(tag: string | null | undefined): string {
  const normalized = String(tag || '').trim().toLowerCase();
  if (!normalized) return '';
  return getSubjectByTag(normalized)?.name || normalized;
}

/** Asignaturas del currículo seleccionables (excluye “general / herramientas”). */
export function getCurriculumSubjectOptions(excludeTag?: string | null): SubjectDefinition[] {
  const exclude = String(excludeTag || '').trim().toLowerCase();
  return SUBJECT_CATALOG.filter(
    (subject) => subject.tag !== 'general' && subject.tag !== exclude,
  ).sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

export function getUseCasesForSubject(subjectId: string): AICaseRecipe[] {
  return SUBJECT_USE_CASES.filter((u) => u.subjectId === subjectId);
}

/**
 * Recetas del Taller interactivo: todas las del catálogo son mecánicas de sala
 * (GameMode). La generación de fichas/guías/rúbricas vive en asignatura o curso.
 */
export function getInteractiveUseCasesForSubject(subjectId: string): AICaseRecipe[] {
  return getUseCasesForSubject(subjectId);
}

/** True si la receta abre un motor de sala (no una ficha imprimible). */
export function isInteractiveRecipe(recipe: AICaseRecipe): boolean {
  return Boolean(recipe.mode);
}

/** Modos constructores relevantes para una asignatura (recetas + motores AI-capable). */
export function getBuilderModesForSubject(subjectId: string): StudioBuilderMode[] {
  const fromRecipes = new Set<StudioBuilderMode>();
  for (const recipe of getUseCasesForSubject(subjectId)) {
    if (isStudioBuilderMode(recipe.mode)) fromRecipes.add(recipe.mode);
  }
  // Motores con generación IA inline disponibles para cualquier asignatura
  const inlineCapable: StudioBuilderMode[] = [
    'arena',
    'poll',
    'order',
    'vincula',
    'sandbox',
    'coop',
    'point',
    'wall',
  ];
  for (const mode of inlineCapable) {
    if (isStudioBuilderMode(mode)) fromRecipes.add(mode);
  }
  return STUDIO_BUILDER_MODES.filter((mode) => fromRecipes.has(mode));
}

export function getUseCaseById(id: string): AICaseRecipe | undefined {
  return SUBJECT_USE_CASES.find((u) => u.id === id);
}

export function buildRecipeUserPrompt(params: {
  recipe: AICaseRecipe;
  subjectName?: string;
  level: string;
  difficulty: string;
  tuning?: string;
}): string {
  const { recipe, subjectName, level, difficulty, tuning } = params;
  const parts = [
    `Receta: "${recipe.title}"`,
    subjectName ? `Asignatura: ${subjectName}` : '',
    `Nivel educativo: ${level}`,
    `Dificultad: ${difficulty}`,
    `Modo de actividad: ${recipe.mode}`,
    tuning?.trim() ? `Detalle del docente: ${tuning.trim()}` : 'Genera contenido automático acorde al nivel y receta.',
  ].filter(Boolean);
  return parts.join('\n');
}

export function buildSubjectAiPrompt(subjectId: string | undefined, userPrompt: string): string {
  if (!subjectId) return userPrompt;
  const subject = getSubjectById(subjectId);
  if (!subject) return userPrompt;
  return `${subject.aiExpertPrompt}\n\nContexto de asignatura: ${subject.name}.\n\nPedido del docente: ${userPrompt}`;
}

export function buildStudioLaunchUrl(useCase: AICaseRecipe, draftId?: string): string {
  const params = new URLSearchParams({ subject: useCase.subjectId, useCase: useCase.id });
  if (draftId) params.set('draft', draftId);
  else if (useCase.presetId) params.set('preset', useCase.presetId);
  return `/studio/${useCase.mode}?${params.toString()}`;
}

export function matchesSubjectTag(draftTag: string, subjectId: string): boolean {
  const subject = getSubjectById(subjectId);
  if (!subject) return false;
  const normalized = draftTag.trim().toLowerCase();
  return (
    normalized === subject.tag ||
    normalized === subject.id ||
    normalized.startsWith(subject.tag.slice(0, 4))
  );
}
